import { useCallback, useEffect, useState } from 'react';
import { History, Loader2, Megaphone, MessageSquare, RefreshCw, Sparkles, Star } from 'lucide-react';
import { FacebookIcon, GoogleBusinessIcon, InstagramIcon, LinkedInIcon } from './PlatformIcon';
import { useBusiness } from '../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../lib/featureFlags';
import { supabase } from '../lib/supabaseClient';

type HistoryEventType = 'manual_reply' | 'auto_reply' | 'post' | 'all';
type HistoryChannel = 'google-business' | 'facebook' | 'instagram' | 'linkedin';

interface HistoryRow {
  id: string;
  channel: HistoryChannel;
  event_type: 'manual_reply' | 'auto_reply' | 'post';
  title: string | null;
  content: string | null;
  review_id: string | null;
  star_rating: number | null;
  tokens_used: number | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

const FILTERS: { id: HistoryEventType; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'manual_reply', label: 'Manual replies' },
  { id: 'auto_reply', label: 'Auto replies' },
  { id: 'post', label: 'Posts' },
];

function eventIcon(type: HistoryRow['event_type']) {
  if (type === 'post') return Megaphone;
  if (type === 'auto_reply') return Sparkles;
  return MessageSquare;
}

function channelIcon(channel: HistoryChannel) {
  if (channel === 'facebook') return FacebookIcon;
  if (channel === 'instagram') return InstagramIcon;
  if (channel === 'linkedin') return LinkedInIcon;
  return GoogleBusinessIcon;
}

function channelLabel(channel: HistoryChannel) {
  if (channel === 'facebook') return 'Facebook';
  if (channel === 'instagram') return 'Instagram';
  if (channel === 'linkedin') return 'LinkedIn';
  return 'Google Business';
}

function isAiPost(row: HistoryRow): boolean {
  if (row.event_type !== 'post') return false;
  const meta = row.metadata || {};
  if (
    meta.ai === true ||
    meta.source === 'ai_auto' ||
    meta.source === 'ai_approved' ||
    meta.source === 'ai_draft' ||
    meta.source === 'ai'
  ) {
    return true;
  }
  const title = (row.title || '').toLowerCase();
  return title.includes('ai post') || title.includes('ai facebook') || title.includes('ai instagram') || title.includes('ai linkedin');
}

function isPendingAiDraft(row: HistoryRow): boolean {
  if (!isAiPost(row)) return false;
  const meta = row.metadata || {};
  return meta.status === 'pending';
}

function eventBadge(type: HistoryRow['event_type'], aiPost = false, pending = false) {
  if (type === 'post') {
    if (pending) return 'bg-amber-50 text-amber-800 border-amber-200';
    return aiPost
      ? 'bg-blue-50 text-blue-800 border-blue-200'
      : 'bg-violet-50 text-violet-700 border-violet-200';
  }
  if (type === 'auto_reply') {
    return 'bg-blue-50 text-blue-800 border-blue-200';
  }
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

function eventLabel(type: HistoryRow['event_type'], aiPost = false, pending = false) {
  if (type === 'post') {
    if (pending) return 'AI post draft';
    return aiPost ? 'AI post' : 'Post';
  }
  if (type === 'auto_reply') return 'Auto reply';
  return 'Manual reply';
}

export default function GoogleBusinessHistory() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [filter, setFilter] = useState<HistoryEventType>('all');
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    if (!businessId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const includePosts = filter === 'all' || filter === 'post';

      let gbpQuery = supabase
        .from('google_business_history')
        .select(
          'id, event_type, title, content, review_id, star_rating, tokens_used, created_at, metadata',
        )
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (filter !== 'all') {
        gbpQuery = gbpQuery.eq('event_type', filter);
      }

      const socialHistoryPromise =
        SOCIAL_ENABLED && includePosts
          ? supabase
              .from('social_post_history')
              .select('id, platform, title, content, tokens_used, created_at, metadata')
              .eq('business_id', businessId)
              .order('created_at', { ascending: false })
              .limit(100)
          : Promise.resolve({ data: [] as any[], error: null });

      const socialDraftsPromise =
        SOCIAL_ENABLED && includePosts
          ? supabase
              .from('social_post_drafts')
              .select('id, platform, content, tokens_used, created_at, source')
              .eq('business_id', businessId)
              .eq('status', 'pending')
              .order('created_at', { ascending: false })
              .limit(50)
          : Promise.resolve({ data: [] as any[], error: null });

      const [
        { data, error },
        { data: draftRows },
        { data: socialRows, error: socialError },
        { data: socialDraftRows, error: socialDraftError },
      ] = await Promise.all([
        gbpQuery,
        includePosts
          ? supabase
              .from('google_business_post_drafts')
              .select('id, content, tokens_used, created_at, cta_type, cta_url')
              .eq('business_id', businessId)
              .eq('status', 'pending')
              .order('created_at', { ascending: false })
              .limit(50)
          : Promise.resolve({ data: [] as any[] }),
        socialHistoryPromise,
        socialDraftsPromise,
      ]);

      if (error) throw error;
      if (socialError) throw socialError;
      if (socialDraftError) throw socialDraftError;

      const historyRows: HistoryRow[] = ((data as Omit<HistoryRow, 'channel'>[]) || []).map(
        (r) => ({
          ...r,
          channel: 'google-business' as const,
        }),
      );

      const loggedDraftIds = new Set(
        historyRows
          .map((r) => (r.metadata as any)?.draft_id)
          .filter(Boolean)
          .map(String),
      );

      for (const d of draftRows || []) {
        if (loggedDraftIds.has(d.id)) continue;
        historyRows.push({
          id: `draft-${d.id}`,
          channel: 'google-business',
          event_type: 'post',
          title: 'AI post draft',
          content: d.content,
          review_id: null,
          star_rating: null,
          tokens_used: d.tokens_used ?? 0,
          created_at: d.created_at,
          metadata: {
            ai: true,
            source: 'ai_draft',
            status: 'pending',
            draft_id: d.id,
          },
        });
      }

      const loggedSocialDraftIds = new Set(
        (socialRows || [])
          .map((r: any) => (r.metadata as any)?.draft_id)
          .filter(Boolean)
          .map(String),
      );

      for (const s of socialRows || []) {
        const platform =
          s.platform === 'instagram' ? 'instagram' : s.platform === 'linkedin' ? 'linkedin' : 'facebook';
        historyRows.push({
          id: `social-${s.id}`,
          channel: platform,
          event_type: 'post',
          title: s.title,
          content: s.content,
          review_id: null,
          star_rating: null,
          tokens_used: s.tokens_used ?? 0,
          created_at: s.created_at,
          metadata: (s.metadata as Record<string, unknown>) || {},
        });
      }

      for (const d of socialDraftRows || []) {
        if (loggedSocialDraftIds.has(d.id)) continue;
        const platform =
          d.platform === 'instagram' ? 'instagram' : d.platform === 'linkedin' ? 'linkedin' : 'facebook';
        const platformName =
          platform === 'instagram' ? 'Instagram' : platform === 'linkedin' ? 'LinkedIn' : 'Facebook';
        const isAi = (d.source || 'ai') === 'ai';
        historyRows.push({
          id: `social-draft-${d.id}`,
          channel: platform,
          event_type: 'post',
          title: isAi
            ? `AI ${platformName} post draft`
            : `${platformName} post draft`,
          content: d.content,
          review_id: null,
          star_rating: null,
          tokens_used: d.tokens_used ?? 0,
          created_at: d.created_at,
          metadata: {
            ai: isAi,
            source: isAi ? 'ai_draft' : 'manual',
            status: 'pending',
            draft_id: d.id,
          },
        });
      }

      historyRows.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      setRows(historyRows);
    } catch (err) {
      console.error('Failed to load media history:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, filter]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors touch-manipulation ${
                filter === f.id
                  ? 'bg-blue-50 border-blue-600 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent' }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void loadHistory()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-16 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl px-6 py-14 text-center">
          <History className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">No history yet</p>
          <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            Replies and posts from Google Business
            {SOCIAL_ENABLED ? ', Facebook, and Instagram' : ''} will show up here.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const aiPost = isAiPost(row);
            const pending = isPendingAiDraft(row);
            const Icon = aiPost ? Sparkles : eventIcon(row.event_type);
            const ChannelIcon = channelIcon(row.channel);
            return (
              <li
                key={row.id}
                className="bg-white border border-gray-200 rounded-2xl p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${eventBadge(row.event_type, aiPost, pending)}`}
                      >
                        {eventLabel(row.event_type, aiPost, pending)}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-gray-50 text-gray-700 border-gray-200">
                        <ChannelIcon className="h-3.5 w-3.5" />
                        {channelLabel(row.channel)}
                      </span>
                      {pending && (
                        <span className="text-[11px] font-medium text-amber-700">
                          Pending approval
                        </span>
                      )}
                      {row.star_rating != null && row.star_rating > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-amber-600 font-medium">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          {row.star_rating}
                        </span>
                      )}
                      {!!row.tokens_used && row.tokens_used > 0 && (
                        <span className="text-[11px] text-gray-400">
                          {row.tokens_used} tokens
                        </span>
                      )}
                      <span className="text-[11px] text-gray-400 ml-auto shrink-0">
                        {new Date(row.created_at).toLocaleString()}
                      </span>
                    </div>
                    {row.title && !pending && (
                      <p className="text-sm font-medium text-gray-900 truncate">{row.title}</p>
                    )}
                    {row.content && (
                      <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">
                        {row.content}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
